import React, { ReactNode } from 'react';

import {
  Header,
  LinkList,
  ListItem,
  ListItemText,
  SideNavDrawer,
  BottomLinkList,
  ListItemButton,
  NavigationContent,
  NavLink,
  DEFAULT_SIDE_NAV_WIDTH,
} from './SideNav.styled';

export interface NavItem {
  /** MUI icon element or any ReactNode displayed above the label. */
  icon: ReactNode;
  /** Text label shown beneath the icon. */
  label: string;
  /** URL passed as `href` to the rendered link element. */
  href: string;
  /** Highlights this item as the current active route. */
  isActive?: boolean;
  /** Overrides the default `target` attribute on the link. */
  target?: string;
}

export interface BottomAction {
  /** MUI icon element or any ReactNode displayed above the label. */
  icon: ReactNode;
  /** Text label shown beneath the icon. */
  label: string;
  /** Called when the action button is clicked. */
  onClick: () => void;
}

export interface SideNavProps {
  /** Navigation items displayed in the main scrollable list. */
  navItems: NavItem[];
  /** Optional content displayed in the header area (e.g. a logo). */
  logo?: ReactNode;
  /** Actions pinned to the bottom of the nav (e.g. Logout). */
  bottomActions?: BottomAction[];
  /** Data test ID for the side nav container. */
  dataTestID?: string;
  /** Side nav width on desktop viewports. Accepts CSS width values such as `64px` or numeric pixels. */
  sideNavWidth?: string | number;
  /**
   * Component used to render each nav item link.
   * Always receives `href` and `target` props.
   * Defaults to a plain `<a>` element.
   *
   * @example React Router adapter
   * ```tsx
   * const NavLinkAdapter = ({ href, ...rest }) => <RouterLink to={href} {...rest} />;
   * <SideNav LinkComponent={NavLinkAdapter} ... />
   * ```
   */
  LinkComponent?: React.ElementType;
}

export function SideNav({
  navItems,
  logo,
  bottomActions = [],
  LinkComponent = 'a',
  dataTestID = 'sideNavContainer',
  sideNavWidth = DEFAULT_SIDE_NAV_WIDTH,
}: SideNavProps) {
  return (
    <React.Fragment>
      <SideNavDrawer data-testid={dataTestID} sideNavWidth={sideNavWidth} variant="permanent" anchor="left" open>
        {logo && <Header>{logo}</Header>}

        <NavigationContent>
          <LinkList>
            {navItems.map((item, index) => (
              <ListItem
                key={`nav-item-${index}-${item.label}`}
                disablePadding
                data-testid={`sidenav-${index}-${item.label}`}
              >
                <NavLink
                  as={LinkComponent}
                  href={item.href}
                  target={item.target ?? (item.href?.startsWith('http') ? '_blank' : '_self')}
                >
                  <ListItemText active={item.isActive} primary={item.icon} secondary={item.label} />
                </NavLink>
              </ListItem>
            ))}
          </LinkList>

          {bottomActions.length > 0 && (
            <BottomLinkList>
              {bottomActions.map((action) => (
                <ListItem key={`bottom-action-${action.label}`} disablePadding>
                  <ListItemButton onClick={action.onClick}>
                    <ListItemText primary={action.icon} secondary={action.label} />
                  </ListItemButton>
                </ListItem>
              ))}
            </BottomLinkList>
          )}
        </NavigationContent>
      </SideNavDrawer>
    </React.Fragment>
  );
}

export default SideNav;
